import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";
import type { LifestyleModule as LifestyleModuleValue } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { LifestyleModule } from "../../blocks/LifestyleModule";
import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

/**
 * toppers GenericPage body assembly: dispatches every block type in the
 * ``Toolbox`` StreamBlock (10 content keys + recursive
 * ``promo_conditional_block``) to its React component, collapsing N
 * ``{% include_block %}`` hydration roots into a single React render tree.
 *
 * NO per-block wrapper: ``generic_page.html`` rendered the body with
 * ``{% for block in self.body %}{% include_block block %}{% endfor %}``,
 * i.e. each block was rendered via its OWN block template with no
 * surrounding ``<div class="block-{type}">``. (That Wagtail wrapper only appears
 * for whole-field ``{{ self.body }}`` rendering, which generic_page did NOT use.)
 * A bare ``<StreamField tagName={Fragment}>`` reproduces that exactly.
 */
/**
 * Generic-page override of the shared ``lifestyle`` dispatch:
 * ``generic_page.html`` rendered the two ``.pdp-lifestyle__content__title``
 * nodes as ``<div>``, whereas PDP — which the shared
 * ``LifestyleModule`` defaults to — used ``<h3>``. Pass ``contentTitleTag="div"``
 * here so the generic page keeps its heading outline while every other
 * shared-map consumer (PDP/Utility/Legal/Home) stays byte-identical.
 */
const genericComponents = {
    ...toolboxComponentsMap,
    lifestyle: (block: { value: Immutable<LifestyleModuleValue> }) => (
        <LifestyleModule value={block.value} contentTitleTag="div" />
    ),
} as const;

export const GenericPageBody = (
    props: Immutable<{
        page: Pick<templates.GenericPageTemplate["page"], "body">;
    }>,
): React.JSX.Element | null => {
    if (!props.page.body) {
        return null;
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.body.value}
            components={genericComponents}
        />
    );
};
